import { useState } from "react";
import { intl } from "LocaleWrapper";
import { useMutation, useQuery } from "@apollo/client";

import { Table, Card, showSnackbar } from "@cofano/cck";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";

import { useConfirm } from "components/hooks/useConfirm";

import { AdminSupportCategory } from "view/admin/utils/admin.types";

import SupportCategoryCreateEditDialog from "../dialogs/SupportCategoryCreateEditDialog";
import SupportCategoryBulkImportDialog from "../dialogs/SupportCategoryBulkImportDialog";

import { GetFaqCategoriesDocument } from "__generated__/view/admin/graphql/admin.queries.generated";
import { DeleteAdminFaqCategoryDocument } from "__generated__/view/admin/graphql/admin.mutations.generated";

const AdminSupportCategories = () => {

	const confirm = useConfirm();

	const { data, refetch, loading } = useQuery(GetFaqCategoriesDocument);
	const [ deleteAdminFaqCategory ] = useMutation(DeleteAdminFaqCategoryDocument);
	const [ bulkImportDialogOpen, openBulkImportDialog ] = useState(false);

	const [ categoryDialog, setCategoryDialog ] = useState<{
		open: boolean;
		category: AdminSupportCategory | null;
	}>({
		open: false,
		category: null,
	});

	const content = (data?.faqCategories ?? []).map(it => ({ ...it, id: it.id ?? undefined }));

	const deleteCategory = (id: number) => {
		deleteAdminFaqCategory({
			variables: {
				id,
			},
		}).then(() => {
			showSnackbar(intl.formatMessage({ id: "SUCCESSFUL_DELETED" }), { variant: "success" });
			refetch();
		});
	};

	return (
		<Card title={intl.formatMessage({ id: "ADMIN_FAQ_CATEGORIES_TITLE" })}>
			<Table
				key={content.length}
				id="adminSupportCategories"
				disableFooter
				contentLoading={loading}
				content={content}
				columns={[ {
					id: "name",
					title: intl.formatMessage({ id: "NAME" }),
				} ]}
				rowActions={[ {
					icon: <EditIcon />,
					onClick: (row: AdminSupportCategory) => setCategoryDialog({ open: true, category: row }),
				}, {
					icon: <DeleteIcon />,
					onClick: (row: AdminSupportCategory) => {
						confirm({
							type: "DELETE",
						}).then(() => {
							if (row.id) {
								deleteCategory(row.id);
							}
						});
					},
				} ]}
				actions={[ {
					title: intl.formatMessage({ id: "ADMIN_FAQ_COPY_DATA" }),
					onClick: () => {
						console.log(content);
						navigator.clipboard.writeText(JSON.stringify(content));
						showSnackbar(intl.formatMessage({ id: "ADMIN_FAQ_COPIED" }), { variant: "success" });
					},
					pinned: true,
				}, {
					title: intl.formatMessage({ id: "ADMIN_FAQ_BULK_IMPORT_ACTION" }),
					onClick: () => openBulkImportDialog(true),
					pinned: true,
				}, {
					title: intl.formatMessage({ id: "ADMIN_FAQ_CATEGORY_ADD" }),
					onClick: () => setCategoryDialog({ open: true, category: null }),
					pinned: true,
				} ]} />

			<SupportCategoryBulkImportDialog
				open={bulkImportDialogOpen}
				onClose={() => openBulkImportDialog(false)}
				refetch={() => refetch()} />

			<SupportCategoryCreateEditDialog
				open={categoryDialog.open}
				category={categoryDialog.category}
				onClose={() => setCategoryDialog({ open: false, category: null })}
				refetch={() => refetch()} />
		</Card>
	);
};

export default AdminSupportCategories;
